import React from 'react';
import { CheckCircle2Icon, MessageCircleIcon, TruckIcon, XIcon } from 'lucide-react';
import { OrderBuilderState } from './useOrderBuilder';
import { formatMXN } from '../../utils/currency';
interface OrderSummaryProps {
  order: OrderBuilderState;
}
const inputClass = 'mt-1.5 block h-12 w-full min-w-0 appearance-none rounded-md border-2 bg-white px-3.5 text-navy-900 placeholder:text-steel-400 focus:border-accent-500 focus:outline-none';
export function OrderSummary({
  order
}: OrderSummaryProps) {
  const {
    lines,
    subtotal,
    totalPieces,
    remainingForFree,
    freeDelivery,
    progress,
    setQty,
    factura,
    setFactura,
    email,
    setEmail,
    ciudad,
    setCiudad,
    attempted,
    errors,
    send
  } = order;
  const emailError = attempted && Boolean(errors.email);
  return <div id="resumen-pedido" className="rounded-xl bg-navy-900 p-4 text-white sm:p-6" data-id="element-178" data-mp="22pl6wzgti9ek">
      <div className="flex items-baseline justify-between gap-3" data-id="element-179" data-mp="1xpfjjvhkz92a">
        <h3 className="font-display text-xl font-semibold" data-id="element-180" data-mp="83kwwex10ezf">Tu pedido</h3>
        {totalPieces > 0 && <span className="text-sm text-steel-400" data-id="element-181" data-mp="c566nre9kd5e">{totalPieces} piezas</span>}
      </div>

      {lines.length === 0 ? <p className="mt-3 rounded-md border border-dashed border-white/15 px-4 py-4 text-sm text-steel-200" data-id="element-182" data-mp="3ubarhtw6xztv">
          Suma piezas con los botones <span className="font-semibold text-white" data-id="element-183" data-mp="32ww51izwjs0">+</span> y aquí verás tu total al
          instante.
        </p> : <ul className="mt-2 max-h-56 divide-y divide-white/10 overflow-y-auto" data-id="element-184" data-mp="3kj51uqtevuhu">
          {lines.map(line => <li key={line.item.id} className="flex items-center gap-2 py-2.5 text-sm" data-id="element-185" data-mp="3bkjmt5n5exlt">
              <div className="min-w-0 flex-1" data-id="element-186" data-mp="3bigpe0jo2272">
                <p className="truncate font-medium text-white" data-id="element-187" data-mp="3gkfogy8dicy7">
                  {line.qty} × {line.item.name}
                </p>
                <p className="truncate text-steel-400" data-id="element-188" data-mp="31o6v2db5yttp">{line.item.spec}</p>
              </div>
              <span className="shrink-0 whitespace-nowrap font-semibold text-white" data-id="element-189" data-mp="36kf213f7jgps">{formatMXN(line.total)}</span>
              <button type="button" onClick={() => setQty(line.item.id, 0)} aria-label={`Quitar ${line.item.name} ${line.item.spec}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-steel-400 transition-colors duration-150 ease-out hover:text-white" data-id="element-190" data-mp="2nien97rc8fej">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-191" data-mp="36eeeggchrex2" />
              </button>
            </li>)}
        </ul>}

      <div className="mt-3 flex items-end justify-between gap-3 border-t border-white/10 pt-3" data-id="element-192" data-mp="31e9xyqbl2k6u">
        <span className="text-sm text-steel-200" data-id="element-193" data-mp="2wjctzxt5v10t">Total estimado</span>
        <span className="whitespace-nowrap font-display text-3xl font-bold leading-none text-white" data-id="element-194" data-mp="2rhdn4dy7plz4">
          {formatMXN(subtotal)}
        </span>
      </div>

      {/* Meta de entrega sin costo */}
      <div className="mt-4 rounded-lg bg-navy-800 p-3" aria-live="polite" data-id="element-195" data-mp="1g28lld9thth3">
        <div className="flex items-start gap-2 text-sm" data-id="element-196" data-mp="3rr4yf4vphg00">
          {freeDelivery ? <>
              <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" data-id="element-197" data-mp="jaq27n0pej1" />
              <span className="font-semibold text-emerald-300" data-id="element-198" data-mp="5i40tsax6rfi">¡Tu entrega va sin costo!</span>
            </> : <>
              <TruckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" aria-hidden="true" data-id="element-199" data-mp="abyjcsesh0d0" />
              <span className="text-steel-200" data-id="element-200" data-mp="fb1cbehhvg4m">
                Te faltan <strong className="text-white" data-id="element-201" data-mp="j9io4bbv1esd">{formatMXN(remainingForFree)}</strong> para entrega sin costo
              </span>
            </>}
        </div>
        <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-white/10" data-id="element-202" data-mp="t8hluyv37q0l">
          <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${freeDelivery ? 'bg-emerald-400' : 'bg-accent-500'}`} style={{
          width: `${Math.max(progress * 100, lines.length ? 4 : 0)}%`
        }} data-id="element-203" data-mp="t6dkanknv33h" />
        </div>
      </div>

      {/* Datos de contacto: una columna, mismo alto y ancho en todos los campos */}
      <div className="mt-5 space-y-3" data-id="element-204" data-mp="ketu7ubm5zyd">
        <div data-id="element-205" data-mp="yd0hyhmumjkb">
          <label htmlFor="order-email" className="block text-sm font-medium text-steel-200" data-id="element-206" data-mp="ybh2gt90ocjl">
            Email <span className="text-accent-500" data-id="element-207" data-mp="ubyibzp0s5ht">*</span>
          </label>
          <input id="order-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" aria-invalid={emailError} aria-describedby="order-email-hint" className={`${inputClass} ${emailError ? 'border-red-400' : 'border-transparent'}`} data-id="element-208" data-mp="6l2u1cjrvsfb" />
          <p id="order-email-hint" className={`mt-1.5 text-xs ${emailError ? 'text-red-300' : 'text-steel-400'}`} data-id="element-209" data-mp="3j0d6nu7n4oov">
            {emailError ? errors.email : 'Te enviamos cotización formal y ficha técnica.'}
          </p>
        </div>

        <div data-id="element-210" data-mp="39o5yel523cb0">
          <label htmlFor="order-city" className="block text-sm font-medium text-steel-200" data-id="element-211" data-mp="39oz6pqjny9v9">
            Ciudad de entrega
          </label>
          <input id="order-city" autoComplete="address-level2" value={ciudad} onChange={e => setCiudad(e.target.value)} placeholder="Ej. León" className={`${inputClass} border-transparent`} data-id="element-212" data-mp="2uwx5o35uxatn" />
        </div>

        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md bg-navy-800 px-3.5 text-sm text-white" data-id="element-213" data-mp="3skpddr2jjufh">
          <input type="checkbox" checked={factura} onChange={e => setFactura(e.target.checked)} className="h-5 w-5 shrink-0 accent-accent-500" data-id="element-214" data-mp="3ioc95z9ddy6h" />
          Necesito factura
        </label>
      </div>

      {attempted && errors.items && <p role="alert" className="mt-3 text-sm text-red-300" data-id="element-215" data-mp="dqgldxmnfaif">
          {errors.items}
        </p>}

      <button type="button" onClick={send} className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-4 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-accent-600 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500" data-id="element-216" data-mp="8r3mufmf0f00">
        <MessageCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-217" data-mp="3m5qissflfrjv" />
        <span className="whitespace-nowrap" data-id="element-218" data-mp="3h6x6v4fyg8lt">Enviar pedido por WhatsApp</span>
      </button>
      <p className="mt-2 text-center text-xs text-steel-400" data-id="element-219" data-mp="3c8vlgcv23x76">Se abre WhatsApp con tu pedido ya escrito. Sin compromiso.</p>
    </div>;
}